import React from 'react';
import clsx from 'clsx';
import { SvgIcon } from 'components/helpers/SvgIcon';
import { PasswordValidationRule } from 'lib/utils/validation/password-validation';
import useDictionary from 'lib/hooks/useDictionary';
import styles from './PasswordValidationRules.module.scss';

export interface PasswordValidationRulesProps {
  rules: PasswordValidationRule[];
  ruleStatus: Record<string, boolean>; // Map of rule id -> pass/fail
  className?: string;
  heading?: string; // Default: "Password must:"
  isEmpty?: boolean; // If true, shows dot icon instead of close icon for failed rules
  id?: string;
}

export default function PasswordValidationRules({
  rules,
  ruleStatus,
  className,
  heading,
  isEmpty = false,
  id,
}: PasswordValidationRulesProps): JSX.Element {
  const { getDictionaryValue } = useDictionary();

  // Use provided heading or construct from dictionary items
  const displayHeading = heading
    ? heading
    : `${getDictionaryValue('Components.MyAccount.PasswordMust', 'Password must')}${getDictionaryValue('Components.MyAccount.LabelSeparator', ':')}`;

  return (
    <div id={id} className={clsx(styles.validationRulesBox, className)}>
      <h3 className={styles.heading}>{displayHeading}</h3>
      <ul className={styles.rulesList} role="list">
        {rules.map((rule) => {
          const isPassed = ruleStatus[rule.id] === true;
          // Resolve dictionary key if available, otherwise use label
          const ruleLabel = rule.dictionaryKey
            ? getDictionaryValue(rule.dictionaryKey, rule.label)
            : rule.label;

          return (
            <li
              key={rule.id}
              className={clsx(styles.ruleItem, { [styles.emptyRuleItem]: isEmpty })}
            >
              {isPassed ? (
                <span className={clsx(styles.checkmarkIconWrapper, styles.ruleIcon)}>
                  <SvgIcon icon="check" viewBox="0 0 16 14" className={styles.checkmarkIcon} />
                </span>
              ) : isEmpty ? (
                <span className={clsx(styles.dotIconWrapper, styles.ruleIcon)}>
                  <span className={styles.dotIcon} />
                </span>
              ) : (
                <span className={clsx(styles.xIconWrapper, styles.ruleIcon)}>
                  <SvgIcon icon="close-small" size="xxs" viewBox="0 0 12 12" />
                </span>
              )}

              <span className={styles.ruleLabel}>{ruleLabel}</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
